import {
  Component, DestroyRef, ElementRef, HostListener, computed, forwardRef, inject, input, signal, viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface DayCell {
  day: number;
  iso: string;      // yyyy-mm-dd
  current: boolean; // pertence ao mês em exibição
  today: boolean;
  selected: boolean;
  /** Fora do intervalo permitido: mostra-se, mas não se escolhe. */
  disabled: boolean;
}

/**
 * Date picker próprio: substitui o `<input type="date">` porque o popup nativo
 * do navegador não é estilizável. Implementa ControlValueAccessor, então funciona
 * com `formControlName`/`ngModel` e troca valores no formato ISO `yyyy-mm-dd`
 * (idêntico ao input nativo — não quebra validações nem cálculos derivados).
 *
 * <p>`min`/`max` fecham o calendário ao intervalo válido: os dias de fora aparecem apagados e
 * não se escolhem, e a navegação de mês pára onde deixa de haver o que escolher. Preferimos
 * isto a deixar escolher e recusar depois — a data da ocorrência não pode ser futura, e não
 * fazia sentido o calendário oferecer o mês que vem para o servidor devolver um erro.
 *
 * <p>O calendário abre como <b>popover</b>, na camada de topo do browser, e posiciona-se em
 * coordenadas do ecrã junto ao campo. Com `position: absolute` ficava preso ao contentor com
 * scroll mais próximo — dentro do painel da ocorrência era cortado a meio — e nenhum `z-index`
 * passa por cima de um `<dialog>` aberto; a camada de topo passa.
 */
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatePicker), multi: true }],
})
export class DatePicker implements ControlValueAccessor {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly popup = viewChild.required<ElementRef<HTMLElement>>('popup');

  /** Onde o calendário se desenha, em coordenadas do ecrã. */
  protected readonly position = signal({ top: 0, left: 0 });

  /** Enquanto aberto, segue o campo — o painel faz scroll e a janela muda de tamanho. */
  private readonly reposition = () => this.place();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopFollowing());
  }

  protected readonly placeholder = 'dd/mm/aaaa';
  protected readonly weekdays = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

  /** Limites do intervalo válido, em ISO `yyyy-mm-dd`. Vazio = sem limite desse lado. */
  readonly min = input<string | null>(null);
  readonly max = input<string | null>(null);

  protected readonly value = signal<string>(''); // ISO yyyy-mm-dd ou ''
  protected readonly open = signal(false);
  protected readonly disabled = signal(false);

  private readonly today = new Date();
  protected readonly viewYear = signal(this.today.getFullYear());
  protected readonly viewMonth = signal(this.today.getMonth()); // 0-based

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private static readonly monthFmt = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

  protected readonly displayValue = computed(() => {
    const v = this.value();
    if (!v) return '';
    const [y, m, d] = v.split('-');
    return `${d}/${m}/${y}`;
  });

  protected readonly monthLabel = computed(() => {
    const label = DatePicker.monthFmt.format(new Date(this.viewYear(), this.viewMonth(), 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  });

  /** Grade de 6 semanas (42 células) do mês em exibição, com dias vizinhos. */
  protected readonly days = computed<DayCell[]>(() => {
    const year = this.viewYear();
    const month = this.viewMonth();
    const selected = this.value();
    const todayIso = DatePicker.iso(this.today);
    const startOffset = new Date(year, month, 1).getDay(); // 0 = domingo
    const cells: DayCell[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(year, month, 1 - startOffset + i);
      const iso = DatePicker.iso(date);
      cells.push({
        day: date.getDate(),
        iso,
        current: date.getMonth() === month,
        today: iso === todayIso,
        selected: iso === selected,
        disabled: this.outOfRange(iso),
      });
    }
    return cells;
  });

  /** "Hoje" só se oferece se hoje for uma escolha válida. */
  protected readonly todayAllowed = computed(() => !this.outOfRange(DatePicker.iso(this.today)));

  /** Não deixa navegar para meses onde não há um único dia por escolher. */
  protected readonly canGoBack = computed(() => {
    const min = this.min();
    if (!min) return true;
    // Último dia do mês anterior: se já é anterior ao mínimo, não há lá nada.
    return DatePicker.iso(new Date(this.viewYear(), this.viewMonth(), 0)) >= min;
  });

  protected readonly canGoForward = computed(() => {
    const max = this.max();
    if (!max) return true;
    // Primeiro dia do mês seguinte.
    return DatePicker.iso(new Date(this.viewYear(), this.viewMonth() + 1, 1)) <= max;
  });

  /**
   * As datas ISO comparam-se como texto: `yyyy-mm-dd` ordena igual em string e em calendário,
   * e assim não se constroem objetos Date só para comparar.
   */
  private outOfRange(iso: string): boolean {
    const min = this.min();
    const max = this.max();
    return (!!min && iso < min) || (!!max && iso > max);
  }

  private static iso(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // ---------- ControlValueAccessor ----------
  writeValue(value: string | null): void {
    this.value.set(value ?? '');
    this.syncView();
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /** Alinha o mês em exibição ao valor selecionado (ou ao mês atual, se vazio). */
  private syncView(): void {
    const v = this.value();
    if (v) {
      const [y, m] = v.split('-').map(Number);
      this.viewYear.set(y);
      this.viewMonth.set(m - 1);
    } else {
      this.viewYear.set(this.today.getFullYear());
      this.viewMonth.set(this.today.getMonth());
    }
  }

  // ---------- Interação ----------
  protected toggle(): void {
    if (this.disabled()) return;
    if (this.open()) {
      this.close();
      return;
    }
    this.syncView();
    this.open.set(true);
    this.popup().nativeElement.showPopover();
    this.place();
    // Capture: o scroll que interessa é o do painel, que não chega à janela por bubbling.
    document.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  protected close(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.stopFollowing();
    this.popup().nativeElement.hidePopover();
    this.onTouched();
  }

  private stopFollowing(): void {
    document.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }

  /** Por baixo do campo; por cima, se em baixo não couber; sempre dentro do ecrã. */
  private place(): void {
    const gap = 6;
    const margin = 8;
    const field = this.trigger().nativeElement.getBoundingClientRect();
    const popup = this.popup().nativeElement;
    const { offsetWidth: width, offsetHeight: height } = popup;

    const below = field.bottom + gap;
    const above = field.top - gap - height;
    const fitsBelow = below + height <= window.innerHeight - margin;
    const top = fitsBelow || above < margin ? below : above;

    const left = Math.min(Math.max(field.left, margin), window.innerWidth - width - margin);
    this.position.set({ top: Math.round(top), left: Math.round(left) });
  }

  protected prevMonth(): void {
    if (!this.canGoBack()) return;
    const month = this.viewMonth() - 1;
    if (month < 0) {
      this.viewMonth.set(11);
      this.viewYear.set(this.viewYear() - 1);
    } else {
      this.viewMonth.set(month);
    }
  }

  protected nextMonth(): void {
    if (!this.canGoForward()) return;
    const month = this.viewMonth() + 1;
    if (month > 11) {
      this.viewMonth.set(0);
      this.viewYear.set(this.viewYear() + 1);
    } else {
      this.viewMonth.set(month);
    }
  }

  protected select(cell: DayCell): void {
    if (cell.disabled) return;
    this.commit(cell.iso);
  }

  protected selectToday(): void {
    if (!this.todayAllowed()) return;
    this.commit(DatePicker.iso(this.today));
  }

  protected clear(): void {
    this.commit('');
  }

  /**
   * Delete e Backspace sobre o campo apagam a data.
   *
   * <p>O campo é um botão — não se seleciona o texto nem se apaga como num `input`, e quem
   * tentava ficava com a ideia de que a data não se podia tirar. O "Limpar" do calendário
   * continua lá; isto é o caminho que se tenta primeiro.
   */
  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    if (!this.value()) return;
    event.preventDefault();
    this.clear();
  }

  private commit(iso: string): void {
    this.value.set(iso);
    this.onChange(iso);
    this.close();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  /** Com o calendário aberto, o Esc fecha só o calendário — não o painel onde ele está. */
  @HostListener('keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (!this.open()) return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }
}
